<template>
	<LoadingIcon v-if="loading" class="aligned-center" />
	<div id="app" v-else>
		<TopNavBar />
		<LgExpandingNav :brandBuilder="brandBuilder" />
		<LgContextNav v-if="showLgContextNav" />
		<section
			class="view"
			:class="{
				expandedRouter: expandedMenu,
				expandedSmallNav: expandedSmallNav,
				'no-subnav':
					$route.matched[0] && $route.matched[0].meta.subNavSpacing === false,
			}"
			v-if="!brandBuilder"
		>
			<transition name="fade" mode="out-in">
				<router-view />
			</transition>
			<TermsOfUse v-if="!termsOfUseAccepted">
				<TermsOfUseSlotComponent slot="modalContent" />
			</TermsOfUse>
			<NonChromeAlert
				v-if="
					($browserDetect.isEdge ||
						$browserDetect.isFirefox ||
						$browserDetect.isOpera ||
						$browserDetect.isIE ||
						$browserDetect.isSafari) &&
					!browserWarningDismissed
				"
			/>
			<UwmFooter />
		</section>
		<ErrorModal
			v-if="globalErrors.showModal"
			@actionClicked="errorModalActionHandler"
			:buttonAction="globalErrors.errorDetails.buttonAction"
			:buttonLabel="globalErrors.errorDetails.buttonLabel"
			:code="globalErrors.errorDetails.code"
			:showDateTime="true"
			:message="globalErrors.errorDetails.message"
			:title="globalErrors.errorDetails.title"
		/>

		<WalkMe />

		<section
			class="view brand-builder"
			:class="{ expandedSmallNav: expandedSmallNav }"
			v-if="brandBuilder"
		>
			<transition name="fade" mode="out-in">
				<router-view />
			</transition>
			<UwmFooter />
		</section>

		<modal
			modalTitle="Review and Save Disclaimers"
			actionBtnName="Close"
			class="lg legal-modal disable-cancel"
			v-if="showModal && showDisclaimerNotificationModal && termsOfUseAccepted"
			@close="closeDisclaimerNotification()"
			@clicked="closeDisclaimerNotification()"
		>
			<div slot="modalContent">
				<p>
					A change you made to your settings may have affected your disclaimers.
					Please review your
					<router-link
						v-on:click.native="closeDisclaimerNotification()"
						to="/preference-center/disclaimers"
						>Disclaimers</router-link
					>
					and update if necessary.
				</p>
			</div>
		</modal>

		<modal
			modalTitle="Feedback"
			class="global-modal md legal-modal disable-padding"
			v-show="showFeedBackModal"
			:show="showFeedBackModal"
			@close="toggleFeedBackModal"
		>
			<div slot="modalContent" class="composer-post-wrapper">
				<Feedback ref="feedback"></Feedback>
			</div>
		</modal>
	</div>
</template>

<script>
import TermsOfUse from '@/components/legal/TermsOfUse.vue';
import TermsOfUseSlotComponent from '@/components/legal/TermsOfUseSlotComponent.vue';
import LgExpandingNav from '@/components/global-navigation/LgExpandingNav.vue';
import TopNavBar from '@/components/global-navigation/TopNavBar.vue';
import LgContextNav from '@/components/global-navigation/LgContextNav.vue';
import UwmFooter from '@/components/global-navigation/UwmFooter.vue';
import WalkMe from '@/components/walkme/WalkMe.vue';
import { mapState, mapActions } from 'vuex';

import NonChromeAlert from '@/components/global/NonChromeAlert.vue';
import Feedback from '@/components/global/Feedback.vue';
import Modal from '@/components/global/Modal.vue';
import LoadingIcon from './components/global/LoadingIcon.vue';
import ErrorModal from '@/components/global/ErrorModal/ErrorModal.vue';
// comment
export default {
	name: 'baseApp',
	components: {
		TermsOfUse,
		TermsOfUseSlotComponent,
		LgExpandingNav,
		TopNavBar,
		LgContextNav,
		UwmFooter,
		WalkMe,
		NonChromeAlert,
		Feedback,
		Modal,
		LoadingIcon,
		ErrorModal,
	},
	data() {
		return {
			showModal: true,
		};
	},
	computed: {
		...mapState({
			expandedMenu: (state) => state.menus.expandedMenu,
			brandBuilder: (state) => state.menus.brandBuilder,
			brandBuilderNav: (state) => state.menus.brandBuilderNav,
			dashboard: (state) => state.menus.dashboard,
			termsOfUseAccepted: (state) =>
				state.profile.termsOfUse ||
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			expandedSmallNav: (state) => state.menus.expandedSmallNav,
			showFeedBackModal: (state) => state.menus.showFeedBackModal,
			showDisclaimerNotificationModal: (state) =>
				state.profile.rolodexModifiedIndicator &&
				!state.profileSwitcher.mainProfileSelector.isInternalUser,
			globalErrors: (state) => state.globalErrors,
			loading: (state) => state.globalLoading.loading,
		}),
		browserWarningDismissed: function () {
			return localStorage.getItem('browserWarningDismissed');
		},
		showLgContextNav: function () {
			return this.$route.matched[0]
				? this.$route.matched[0].meta.showSubNav
				: false;
		},
	},
	methods: {
		...mapActions([
			'closeErrorModal',
			'toggleBrandBuilder',
			'toggleDashboard',
			'toggleClientConnect',
			'toggleFeedBackModal',
			'resetUserRolodexModified',
		]),
		showContextMenu() {
			return !(this.brandBuilder === true || this.dashboard === true);
		},
		closeDisclaimerNotification() {
			this.resetUserRolodexModified();
			this.showModal = false;
		},
		errorModalActionHandler({ buttonAction }) {
			switch (buttonAction) {
				case 'close':
					this.closeErrorModal();
					break;
				case 'reload':
					location.reload();
					break;
				case 'routerPush':
					this.$router.push({
						path: this.globalErrors.errorDetails.path,
					});
					this.closeErrorModal();
					break;
				default:
					this.closeErrorModal();
			}
		},
	},
};
</script>
<style lang="scss">
@import '@/SCSS/styles.scss';

html,
body {
	margin: 0;
	padding: 0;
	@include typo--roboto__regular;
}

.aligned-center {
	@include flex-center;
	height: 100vh;
}

.view {
	margin-top: $top-nav-height;
	padding-top: $context-nav-height;
	background-color: $off-white;

	& > :nth-child(1) {
		// helps position footer
		min-height: calc(100vh - #{$top-nav-height + $context-nav-height + 46});
	}

	&.brand-builder {
		margin-top: calc(#{$top-nav-height} + 1px);
		padding-top: 0;
		margin-left: $brand-builder-width;

		@include sm {
			margin-left: 0;
		}
	}

	.content {
		padding: $base-padding/2;
		display: flex;
		justify-content: center;
		flex-wrap: wrap;

		img {
			padding: $base-padding/2;
		}
	}

	@include lg {
		margin-left: $collapsed-menu-width;
		margin-top: $top-nav-height + $context-nav-height;
		padding-top: 0;
	}

	&.expandedSmallNav {
		display: none;
	}

	&.expandedRouter {
		@include lg {
			margin-left: $expanded-menu-width;
		}
	}

	&.no-subnav {
		margin-top: $top-nav-height;
		padding-top: 0;

		& > :nth-child(1) {
			// helps position footer, which is taller on smaller screens
			min-height: calc(100vh - #{$top-nav-height + $context-nav-height + 39});

			@media screen and (min-width: 1120px) {
				min-height: calc(100vh - #{$top-nav-height + $context-nav-height + 24});
			}
		}
	}
}

.brand-builder {
	position: relative;
	z-index: 12;

	.b360-footer {
		margin-left: calc(418px + (20px * 2));
		position: relative;
		z-index: 1;

		@media only screen and (max-width: 1374px) {
			flex-direction: column;
			align-items: center;
			text-align: center;

			ul li:first-child:before {
				display: none;
			}
		}

		@media only screen and (max-width: $breakpoint) {
			margin-left: 0;
		}
	}
}

.noscroll {
	overflow: hidden;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity $b360-motion-duration--shortest
		$b360-motion-easing--standard;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}
</style>
